import { useEffect, useState } from "react";
import { Trash2, Plus, LogOut, Download, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "../lib/api";

const inputCls = "w-full border border-[#0A1128]/15 bg-white px-4 py-3 text-sm outline-none focus:border-[#1D4ED8] transition-colors";
const INDUSTRIES = ["Technology", "Finance", "Healthcare", "Engineering", "Marketing", "Retail", "Legal", "Consulting"];
const TYPES = ["Permanent", "Temporary", "Contract", "Executive"];
const EMPTY_JOB = { title: "", company: "", location: "", industry: "Technology", type: "Permanent", salary: "", description: "", featured: false };

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [jobForm, setJobForm] = useState(EMPTY_JOB);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const token = () => localStorage.getItem("fam_admin_token");

  useEffect(() => {
    (async () => {
      if (!token()) return setChecking(false);
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        localStorage.removeItem("fam_admin_token");
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!user) return;
    loadJobs();
    loadEnquiries();
  }, [user]);

  const loadJobs = async () => {
    const { data } = await api.get("/jobs");
    setJobs(data);
  };

  const loadEnquiries = async () => {
    try {
      const { data } = await api.get("/enquiries");
      setEnquiries(data);
    } catch { /* ignore */ }
  };

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", loginForm);
      localStorage.setItem("fam_admin_token", data.token);
      setUser(data.user);
      toast.success("Welcome back, admin.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("fam_admin_token");
    setUser(null);
  };

  const saveJob = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/jobs/${editingId}`, jobForm);
        toast.success("Job updated.");
      } else {
        await api.post("/jobs", jobForm);
        toast.success("Job published.");
      }
      setJobForm(EMPTY_JOB);
      setEditingId(null);
      loadJobs();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const removeJob = async (id) => {
    try {
      await api.delete(`/jobs/${id}`);
      toast.success("Job removed.");
      loadJobs();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (checking) {
    return <div className="min-h-screen bg-[#0A1128] flex items-center justify-center text-white/50 text-sm uppercase tracking-[0.2em]" data-testid="admin-loading">Loading…</div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0A1128] flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="w-full max-w-md">
          <img src="/fam-logo.png" alt="FAM" className="h-14 w-auto mb-10" />
          <h1 className="font-display font-bold text-4xl tracking-tight text-white mb-2">Admin Login</h1>
          <p className="text-white/50 text-sm mb-10">Manage job listings and view candidate enquiries.</p>
          <form onSubmit={login} className="space-y-4" data-testid="admin-login-form">
            <input required type="email" placeholder="Admin email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} className={inputCls} data-testid="admin-login-email" />
            <input required type="password" placeholder="Password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} className={inputCls} data-testid="admin-login-password" />
            <button disabled={busy} className="w-full bg-[#1D4ED8] hover:bg-[#1E3A8A] disabled:opacity-50 text-white text-xs uppercase tracking-[0.25em] py-4 transition-colors" data-testid="admin-login-submit">
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>
          <a href="/" className="block mt-8 text-xs uppercase tracking-[0.2em] text-white/40 hover:text-white transition-colors" data-testid="admin-back-home">← Back to website</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F5F7]" data-testid="admin-dashboard">
      <header className="bg-[#0A1128] px-6 md:px-12 py-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src="/fam-logo.png" alt="FAM" className="h-9 w-auto" />
          <span className="text-white font-display font-bold tracking-tight">Admin Console</span>
        </div>
        <div className="flex items-center gap-4">
          <a href="/" className="text-white/50 hover:text-white text-xs uppercase tracking-[0.2em] transition-colors" data-testid="admin-view-site">View Site</a>
          <button onClick={logout} className="flex items-center gap-2 text-white/50 hover:text-white text-xs uppercase tracking-[0.2em] transition-colors" data-testid="admin-logout">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <div className="px-6 md:px-12 py-8">
        <div className="flex border border-[#0A1128]/15 bg-white mb-8 w-fit" data-testid="admin-tabs">
          {["jobs", "enquiries"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-8 py-3 text-xs uppercase tracking-[0.2em] transition-colors ${tab === t ? "bg-[#0A1128] text-white" : "text-[#475569]"}`}
              data-testid={`admin-tab-${t}`}
            >
              {t === "jobs" ? `Jobs (${jobs.length})` : `Enquiries (${enquiries.length})`}
            </button>
          ))}
        </div>

        {tab === "jobs" ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <form onSubmit={saveJob} className="lg:col-span-4 bg-white border border-[#0A1128]/10 p-8 space-y-4 h-fit" data-testid="admin-job-form">
              <h2 className="font-display font-bold text-xl tracking-tight text-[#0A1128] mb-2">
                {editingId ? "Edit Job" : "Add New Job"}
              </h2>
              <input required placeholder="Job title" value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })} className={inputCls} data-testid="job-form-title" />
              <input required placeholder="Company" value={jobForm.company} onChange={(e) => setJobForm({ ...jobForm, company: e.target.value })} className={inputCls} data-testid="job-form-company" />
              <div className="grid grid-cols-2 gap-4">
                <input required placeholder="Location" value={jobForm.location} onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })} className={inputCls} data-testid="job-form-location" />
                <input placeholder="Salary (e.g. ₹10–15 LPA)" value={jobForm.salary} onChange={(e) => setJobForm({ ...jobForm, salary: e.target.value })} className={inputCls} data-testid="job-form-salary" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <select value={jobForm.industry} onChange={(e) => setJobForm({ ...jobForm, industry: e.target.value })} className={inputCls} data-testid="job-form-industry">
                  {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
                </select>
                <select value={jobForm.type} onChange={(e) => setJobForm({ ...jobForm, type: e.target.value })} className={inputCls} data-testid="job-form-type">
                  {TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <textarea rows={3} placeholder="Short description" value={jobForm.description} onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })} className={inputCls} data-testid="job-form-description" />
              <label className="flex items-center gap-3 text-sm text-[#475569]">
                <input type="checkbox" checked={jobForm.featured} onChange={(e) => setJobForm({ ...jobForm, featured: e.target.checked })} data-testid="job-form-featured" />
                Mark as featured
              </label>
              <div className="flex gap-3">
                <button disabled={busy} className="flex-1 flex items-center justify-center gap-2 bg-[#1D4ED8] hover:bg-[#1E3A8A] disabled:opacity-50 text-white text-xs uppercase tracking-[0.2em] py-3 transition-colors" data-testid="job-form-submit">
                  <Plus size={14} /> {editingId ? "Update Job" : "Publish Job"}
                </button>
                {editingId && (
                  <button type="button" onClick={() => { setEditingId(null); setJobForm(EMPTY_JOB); }} className="px-5 border border-[#0A1128]/15 text-xs uppercase tracking-[0.2em] text-[#475569]" data-testid="job-form-cancel">
                    Cancel
                  </button>
                )}
              </div>
            </form>

            <div className="lg:col-span-8 bg-white border border-[#0A1128]/10" data-testid="admin-jobs-list">
              {jobs.map((j) => (
                <div key={j.id} className="flex items-center justify-between gap-4 px-6 py-5 border-b border-[#0A1128]/10 last:border-b-0" data-testid={`admin-job-row-${j.id}`}>
                  <div>
                    <p className="font-display font-bold text-[#0A1128]">{j.title} {j.featured && <span className="text-[9px] uppercase tracking-[0.2em] bg-[#1D4ED8] text-white px-1.5 py-0.5 ml-2 align-middle">Featured</span>}</p>
                    <p className="text-xs text-[#475569] mt-1">{j.company} · {j.location} · {j.industry} · {j.type}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => { setEditingId(j.id); setJobForm({ title: j.title, company: j.company, location: j.location, industry: j.industry, type: j.type, salary: j.salary, description: j.description, featured: j.featured }); }} className="p-2 border border-[#0A1128]/15 text-[#475569] hover:text-[#1D4ED8] hover:border-[#1D4ED8] transition-colors" data-testid={`admin-edit-job-${j.id}`} aria-label="Edit">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => removeJob(j.id)} className="p-2 border border-[#0A1128]/15 text-[#475569] hover:text-red-600 hover:border-red-600 transition-colors" data-testid={`admin-delete-job-${j.id}`} aria-label="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
              {jobs.length === 0 && <p className="p-10 text-center text-[#475569] text-sm">No jobs yet. Publish your first role.</p>}
            </div>
          </div>
        ) : (
          <div>
            <div className="flex justify-end mb-4">
              <a
                href={`${API}/enquiries/export?token=${token()}`}
                className="flex items-center gap-2 bg-[#0A1128] hover:bg-[#1D4ED8] text-white text-xs uppercase tracking-[0.2em] px-6 py-3 transition-colors"
                data-testid="admin-export-csv"
              >
                <Download size={14} /> Download Data (CSV)
              </a>
            </div>
          <div className="bg-white border border-[#0A1128]/10" data-testid="admin-enquiries-list">
            {enquiries.map((en) => (
              <div key={en.id} className="px-6 py-5 border-b border-[#0A1128]/10 last:border-b-0" data-testid={`admin-enquiry-${en.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-display font-bold text-[#0A1128]">
                      {en.name}
                      <span className={`text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 ml-2 align-middle ${en.kind === "contact" ? "bg-[#0A1128] text-white" : "bg-[#1D4ED8] text-white"}`}>
                        {en.kind}
                      </span>
                    </p>
                    <p className="text-xs text-[#475569] mt-1">{en.email} {en.phone && `· ${en.phone}`} {en.job_title && `· Applied: ${en.job_title}`}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-[10px] text-[#94A3B8] uppercase tracking-[0.15em]">{new Date(en.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
                    {en.resume_path && (
                      <a
                        href={`${API}/files/${en.resume_path}?token=${token()}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1.5 text-xs text-[#1D4ED8] hover:underline"
                        data-testid={`admin-resume-download-${en.id}`}
                      >
                        <Download size={13} /> {en.resume_filename || "Resume"}
                      </a>
                    )}
                  </div>
                </div>
                {en.message && <p className="text-sm text-[#475569] mt-3 font-light">{en.message}</p>}
              </div>
            ))}
            {enquiries.length === 0 && <p className="p-10 text-center text-[#475569] text-sm">No enquiries yet.</p>}
          </div>
          </div>
        )}
      </div>
    </div>
  );
}
